import { synapse, useStoreSelector } from '@ublique/platform'
import { useEffect } from 'react'

/**
 * Create a store from an initial state.
 *
 * @param {object} initialState The initial state
 * @returns {object} The new store
 */
export const createStore = (initialState) => {
  return synapse.store(initialState)
}

/**
 * Update a specific slice of the store.
 *
 * @param {object} store Store object
 * @param {object} updatedData Updated data
 */
export const updateState = (store, updatedData) => {
  store.update((prevState) => ({ ...prevState, ...updatedData }))
}

/**
 * Update a specific slice of the store using the reducer function, useful when using a custom update function.
 *
 * @param {object} store Store object
 * @param {Function} reducer Reducer function
 */
export const updateStateWithReducer = (store, reducer) => {
  store.update(reducer)
}

/**
 * Hook to get data from ta specific slice of the store.
 *
 * @param {string} key Store key
 * @param {Function} selector Selector function
 * @returns
 */
export const useSelectState = (key, selector) => {
  return useStoreSelector(key, selector)
}

/**
 * Get data from ta specific slice of the store.
 *
 * @param {object} store Store
 * @param {string | Function} selector Store key or selector function
 * @returns
 */
export const selectState = (store, selector) => {
  return store.get(selector)
}

/**
 * Execute an action and eventually update the store and execute a callback
 *
 * @param {Promise} action The action to perform, a promise that returns something (e.g. an API call)
 * @param {object} param1.store The store object to update
 * @param {string} param1.storeKey The store key to update
 * @param {Function} param1.successCallback The callback to execute after the action that receives the result of the action
 * @param {Function} param1.failCallback The callback to execute when an error is caught
 *
 * @returns {object | Error} Action result
 */
export const executeAction = async (
  action,
  { store, storeKey, successCallback, failCallback, reducer } = {}
) => {
  try {
    const result = await action()

    if (store) {
      const updatedData = storeKey ? { [storeKey]: result } : result
      updateState(store, updatedData)
    }

    if (typeof reducer === 'function') {
      updateStateWithReducer(store, reducer)
    }

    if (typeof successCallback === 'function') {
      successCallback(result)
    }

    return result
  } catch (error) {
    if (typeof failCallback === 'function') {
      failCallback(error)
    }

    return error
  }
}

export const useResetState = (initialState = {}, store) => {
  useEffect(() => {
    return () => {
      updateState(store, initialState)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
